import {
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChildren,
} from "@angular/core";
import {
  FormControlName,
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from "@angular/forms";
import { FloatLabelType } from "@angular/material/form-field";
import { Router } from "@angular/router";
import { User } from "src/app/core/models/user.module";
import { UserService } from "src/app/core/services/user.service";
import { ConfirmPasswordValidator } from "./components/confirm-password.validator";
import Swal from "sweetalert2";
import { GoogleAuthService } from "src/app/core/services/google-auth.service";
import { JwtService } from "src/app/core/services/jwt.service";

@Component({
  selector: "app-signup",
  templateUrl: "./signup.component.html",
  styleUrls: ["./signup.component.css"],
})
export class SignupComponent implements OnInit {
  @ViewChildren(FormControlName, { read: ElementRef })
  formControls: ElementRef[];

  // Email Signup
  hide = true;
  hideConfirmation = true;

  submitted = false;

  signupForm: FormGroup;

  errorStatus: boolean;
  extractedErrorMessage: any;
  extractedSuccessMessage: any;

  isLoading: boolean = false;
  isDisabled: boolean = false;
  isLoadingGoogle: boolean = false;
  isDisabledGoogle: boolean = false;

  userData: any;
  userFirstNameGoogle: any;
  userLastNameGoogle: any;

  passwordControl: any;
  confirmPasswordControl: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private userService: UserService,
    private googleAuthService: GoogleAuthService,
    private _ngZone: NgZone,
    private jwt: JwtService
  ) {}

  ngOnInit(): void {
    const token = localStorage.getItem("workpass_token");

    if (token) {
      this.router.navigate(["/user/applicant-profilling"]);
    } else {
      [this.router.navigate(["/signup"])];
    }

    this.signupForm = this.fb.group(
      {
        firstName: ["", Validators.required],
        lastName: ["", Validators.required],
        email: [
          "",
          [
            Validators.required,
            Validators.email,
            Validators.pattern(".*\\.com$"),
          ],
        ],
        password: ["", [Validators.required, Validators.minLength(8)]],
        ConfirmPassword: ["", [Validators.required, Validators.minLength(8)]],
      },
      {
        validator: ConfirmPasswordValidator("password", "ConfirmPassword"),
      }
    );
  }

  onNavigateLogin() {
    this.router.navigate(["login"]);
  }

  addUser() {
    // Set isLoading to true to disable the signup button
    this.isLoading = true;
    this.isDisabled = true;

    const addUserRequest: User = {
      email: this.signupForm.value.email,
      firstName: this.signupForm.value.firstName,
      lastName: this.signupForm.value.lastName,
      password: this.signupForm.value.password,
    };

    if (this.signupForm.valid) {
      this.isDisabled = true;

      this.userService.register(addUserRequest).subscribe(
        (response) => {
          this.isDisabled = false;

          Swal.fire({
            position: "center",
            icon: "success",
            title: "Registration Successful",
            showConfirmButton: false,
            showCloseButton: true,
          }).then(() => {
            this.router.navigate(["/verify-email"]);
          });
        },
        (error) => {
          this.isDisabled = true;

          if (
            error.error.message ==
            "The user with the provided email already exists (EMAIL_EXISTS)."
          ) {
            Swal.fire({
              position: "center",
              icon: "error",
              title: "User already exist",
              showConfirmButton: false,
              showCloseButton: true,
            });
          }

          if (error.error.message == undefined) {
            Swal.fire({
              position: "center",
              icon: "error",
              title: "Please try again later",
              showConfirmButton: false,
              showCloseButton: true,
            });
          }

          this.isLoading = false;
        }
      );
    } else {
      const password = this.signupForm.get("password");
      const confirmPassword = this.signupForm.get("ConfirmPassword");
      const firstName = this.signupForm.get("firstName").value;
      const lastName = this.signupForm.get("lastName").value;
      const email = this.signupForm.get("email").value;

      if (
        !firstName ||
        !lastName ||
        !email ||
        !password.value ||
        !confirmPassword.value
      ) {
        Swal.fire({
          position: "center",
          icon: "error",
          title: "Please fill all required fields",
          showConfirmButton: false,
          showCloseButton: true,
        });

        this.isDisabled = false;
      } else if (
        password.value.length < 8 ||
        confirmPassword.value.length < 8
      ) {
        this.isDisabled = false;

        Swal.fire({
          position: "center",
          icon: "error",
          title: "Password must be at least 8 characters long",
          showConfirmButton: false,
          showCloseButton: true,
        });
      } else if (confirmPassword.hasError("ConfirmPasswordValidator")) {
        // ...
        this.isDisabled = false;

        Swal.fire({
          position: "center",
          icon: "error",
          title: "Passwords do not match",
          showConfirmButton: false,
          showCloseButton: true,
        });
      }

      this.signupForm.markAllAsTouched();
      this.isLoading = false;
    }
  }

  tryGoogle() {
    this.isDisabledGoogle = true;

    this.googleAuthService.GoogleAuth().then((result) => {
      this._ngZone.run(() => {
        const userData = {
          idToken: result.user._delegate.accessToken,
          firstName: result.additionalUserInfo.profile.given_name,
          lastName: result.additionalUserInfo.profile.family_name,
        };

        this.userFirstNameGoogle = userData.firstName;
        this.userLastNameGoogle = userData.lastName;

        this.isLoadingGoogle = true;
        this.isDisabledGoogle = true;

        this.userService.googleLogin(userData).subscribe((response) => {
          this.isLoadingGoogle = false;
          this.isDisabledGoogle = false;

          const workpass_token = response.data.token;

          this.jwt.saveToken(workpass_token);

          this.userService
            .getApplicantDetails(response.data.id)
            .subscribe((response) => {
              this.userData = response;

              if (this.userData.loginProvider === 0) {
                const updatedUserData = { ...this.userData };

                updatedUserData.loginProvider = 2;

                this.userService
                  .updateApplicantDetails(updatedUserData)
                  .subscribe((result) => {
                    localStorage.setItem("userID", response.id.toString());
                    localStorage.setItem(
                      "applicantDetails",
                      JSON.stringify(updatedUserData)
                    );

                    if (response.isProfileComplete == false) {
                      this.router.navigate(["/user/applicant-profilling"]);

                      this.isLoadingGoogle = false;
                      this.isDisabledGoogle = false;
                    } else {
                      this.isLoadingGoogle = false;
                      this.isDisabledGoogle = false;

                      this.router.navigate(["/user/home"]);
                    }
                  });
              } else {
                localStorage.setItem("userID", response.id.toString());
                localStorage.setItem(
                  "applicantDetails",
                  JSON.stringify(response)
                );

                if (response.isProfileComplete == false) {
                  this.router.navigate(["/user/applicant-profilling"]);

                  this.isLoadingGoogle = false;
                  this.isDisabledGoogle = false;
                } else {
                  this.router.navigate(["/user/home"]);

                  this.isLoadingGoogle = false;
                  this.isDisabledGoogle = false;
                }
              }
            });
        });
      });
    });
  }
}
